import Axios from "axios";
import useAuthStore from "../../stores/useAuthStore";
import CWOpenDrawer from "./CWOpenDrawer";
import { processReceiptTemplate } from "./templateProcessor";

// Default template, in the same elements[]/type/{{mustache}}/table schema as
// printTemplate/tillCloseTemplate/salesReportTemplate (processed by the shared
// templateProcessor.js). Used only when the till has no storeCloseTemplate configured yet.
const buildDefaultTemplate = () => ({
  elements: [
    { type: "text", content: "DAY END CLOSING REPORT", align: "center", style: ["bold", "large"] },
    { type: "spacing", lines: 1 },
    { type: "text", content: "Branch: {{branch}}", align: "center" },
    { type: "text", content: "Opened At: {{openingTime}}", align: "center" },
    { type: "text", content: "Closed At: {{closingTime}}", align: "center" },
    { type: "text", content: "User: {{userName}}", align: "center" },
    { type: "spacing", lines: 1 },
    { type: "text", content: "Tax Details", align: "left", style: ["bold"] },
    {
      type: "table",
      columns: [
        { title: "Tax Type", width: 20, alignment: "left" },
        { title: "NET", width: 11, alignment: "left", format: "currency" },
        { title: "Tax", width: 11, alignment: "left", format: "currency" },
      ],
      startPositions: [0, 20, 31],
      data: "{{taxDetails}}",
      dataMapping: { taxType: "taxType", net: "net", tax: "tax" },
    },
    { type: "text", content: "Gross Total:", align: "left", length: 15, startPosition: 0, addNewline: false },
    { type: "text", content: "{{grossTotal}}", align: "right", length: 15 },
    { type: "spacing", lines: 1 },
    { type: "text", content: "Details of Payment", align: "left", style: ["bold"] },
    {
      type: "table",
      columns: [
        { title: "Mode of Payment", width: 20, alignment: "left" },
        { title: "Inv Count", width: 10, alignment: "center" },
        { title: "Gross Amt", width: 12, alignment: "left", format: "currency" },
      ],
      startPositions: [0, 20, 30],
      data: "{{paymentDetails}}",
      dataMapping: { paymentName: "paymentName", orderCount: "orderCount", totalAmount: "totalAmount" },
    },
    { type: "spacing", lines: 1 },
    { type: "text", content: "Cash Account", align: "left", style: ["bold"] },
    {
      type: "table",
      columns: [
        { title: "Mode", width: 15, alignment: "left" },
        { title: "Actual", width: 9, alignment: "left", format: "currency" },
        { title: "System", width: 9, alignment: "left", format: "currency" },
        { title: "Diff", width: 9, alignment: "left", format: "currency" },
      ],
      startPositions: [0, 15, 24, 33],
      data: "{{cashAccount}}",
      dataMapping: { mode: "mode", actual: "actual", system: "system", diff: "diff" },
    },
    { type: "spacing", lines: 1 },
    { type: "text", content: "Note Counting", align: "left", style: ["bold"] },
    {
      type: "table",
      columns: [
        { title: "Denomination", width: 15, alignment: "left" },
        { title: "Quantity", width: 10, alignment: "center" },
        { title: "Total", width: 17, alignment: "left", format: "currency" },
      ],
      startPositions: [0, 15, 25],
      data: "{{noteCounting}}",
      dataMapping: { denomination: "denomination", quantity: "quantity", total: "total" },
    },
    { type: "spacing", lines: 1 },
    { type: "text", content: "Total Amount", align: "left", length: 25, startPosition: 0, style: ["bold"], addNewline: false },
    { type: "text", content: "{{totalAmount}}", align: "right", length: 14, style: ["bold"] },
    { type: "spacing", lines: 1 },
    { type: "text", content: "Cash Summary", align: "left", style: ["bold"] },
    { type: "text", content: "Opening Balance", align: "left", length: 25, startPosition: 0, style: ["bold"], addNewline: false },
    { type: "text", content: "{{openingBalance}} AED", align: "right", length: 14, style: ["bold"] },
    { type: "text", content: "Cash Sales", align: "left", length: 25, startPosition: 0, style: ["bold"], addNewline: false },
    { type: "text", content: "{{cashSales}}", align: "right", length: 14, style: ["bold"] },
    { type: "text", content: "Cash In", align: "left", length: 25, startPosition: 0, style: ["bold"], addNewline: false },
    { type: "text", content: "{{cashIn}}", align: "right", length: 14, style: ["bold"] },
    { type: "text", content: "Cash Out", align: "left", length: 25, startPosition: 0, style: ["bold"], addNewline: false },
    { type: "text", content: "{{cashOut}}", align: "right", length: 14, style: ["bold"] },
    { type: "text", content: "Closing Balance", align: "left", length: 25, startPosition: 0, style: ["bold"], addNewline: false },
    { type: "text", content: "{{closingBalance}}", align: "right", length: 14, style: ["bold"] },
    { type: "text", content: "Diff", align: "left", length: 25, startPosition: 0, style: ["bold"], addNewline: false },
    { type: "text", content: "{{diff}}", align: "right", length: 14, style: ["bold"] },
    { type: "spacing", lines: 1 },
    { type: "text", content: "Cash To Keep", align: "left", length: 25, startPosition: 0, style: ["bold"], addNewline: false },
    { type: "text", content: "{{carryForward}}", align: "right", length: 14, style: ["bold"] },
    { type: "spacing", lines: 5 },
  ],
});

// customTemplateJson: storeCloseTemplate.xmlcode2 when the till has that key configured
// (cwController="Y"), a JSON string in the elements[]/type/{{mustache}} shape shared with
// printTemplate/tillCloseTemplate/salesReportTemplate. Falls back to buildDefaultTemplate()
// when not configured or invalid, so CW tills without a backend template still print something.
const DayEndReportCWHCPrint = async (printReciptData, customTemplateJson) => {
  const tillData = useAuthStore.getState().tillData;
  const printerURL = tillData?.tillAccess?.cwrTill?.hardwareController?.imageUrl;
  const pricePrecision = tillData?.tillAccess?.csBunit?.currencies?.[0]?.prcPrecision ?? 2;

  let template = buildDefaultTemplate();
  if (customTemplateJson) {
    try {
      template = JSON.parse(customTemplateJson);
    } catch (error) {
      console.error("[DayEndReportCWHCPrint] Invalid storeCloseTemplate JSON, using default template:", error?.message);
      template = buildDefaultTemplate();
    }
  }

  const fmt = (n) => parseFloat(n || 0).toFixed(pricePrecision);

  const cashOut = fmt(parseFloat(printReciptData.cashInOutSummary.cashOut) + parseFloat(printReciptData.cashInOutSummary.pettCashOut));
  const cashIn = fmt(parseFloat(printReciptData.cashInOutSummary.cashIn) + parseFloat(printReciptData.cashInOutSummary.pettCashIn));

  const taxDetails = (printReciptData.taxDetailsSummary || []).map((tax) => ({
    taxType: tax.taxType,
    net: fmt(tax.net),
    tax: fmt(tax.tax),
  }));

  const paymentDetails = (printReciptData.paymentsSummary || []).map((p) => ({
    paymentName: p.paymentName,
    orderCount: String(p.orderCount),
    totalAmount: fmt(p.totalAmount),
  }));

  // Cash row uses closingBalance/systemClosingbalance/closingBalanceDiff (matches the till-close
  // "Cash Account" convention); every other payment method's Actual/System are both its own total
  // with a zero diff (no independent count for non-cash tenders).
  const cashAccount = (printReciptData.paymentsSummary || []).map((p) => {
    if (p.paymentName === "Cash") {
      return {
        mode: p.paymentName,
        actual: fmt(printReciptData.closingBalance),
        system: fmt(printReciptData.systemClosingbalance),
        diff: fmt(printReciptData.closingBalanceDiff),
      };
    }
    return {
      mode: p.paymentName,
      actual: fmt(p.totalAmount),
      system: fmt(p.totalAmount),
      diff: fmt(0),
    };
  });

  const noteCounting = [...(printReciptData.denominationsSummary || [])]
    .sort((a, b) => b.value - a.value)
    .map((d) => ({
      denomination: d.value.toFixed(2),
      quantity: String(d.count),
      total: fmt(d.amount),
    }));

  const data = {
    branch: printReciptData.branch,
    openingTime: printReciptData.openedAt,
    closingTime: printReciptData.closedAt,
    userName: printReciptData.userName,
    grossTotal: fmt(printReciptData.taxGrossTotal),
    totalAmount: fmt(printReciptData.denominationsTotal),
    openingBalance: fmt(printReciptData.openingBalance),
    cashSales: fmt(printReciptData.cashSales),
    cashIn,
    cashOut,
    closingBalance: fmt(printReciptData.systemClosingbalance),
    diff: fmt(printReciptData.closingBalanceDiff),
    carryForward: fmt(printReciptData.closingBalanceCarryForward),
    taxDetails,
    paymentDetails,
    cashAccount,
    noteCounting,
  };

  const processedTemplate = processReceiptTemplate(template, data);
  const printPayload = JSON.stringify(processedTemplate, null, 2);

  Axios.post(`${printerURL}printer`, printPayload, {
    headers: {
      "Content-Type": "text/plain",
    },
  })
    .then(() => {
      CWOpenDrawer("dayEnd");
    })
    .catch(() => {});

  return processedTemplate;
};

export default DayEndReportCWHCPrint;
